import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getConsentRequest, submitSignedConsent, submitSignedConsentDirect, getCompanyById, getPreparerById } from '../firestoreHelper';

// Helper to compress image to reduce size for Firestore storage
const compressImage = (dataUrl, maxWidth = 600, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
};

function TaxpayerConsentForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [consentData, setConsentData] = useState(null);
  const [company, setCompany] = useState(null);
  const [preparer, setPreparer] = useState(null);
  
  // Responsive state - properly updates on window resize
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobileView = windowWidth <= 768;
  
  // Steps: 'rules' -> 'form' -> 'review' -> 'submitted'
  const [currentStep, setCurrentStep] = useState('rules');
  const [rulesAccepted, setRulesAccepted] = useState(false);
  
  // Custom fields from preparer
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  
  // Photo ID uploads for address verification
  const [photoIDPreview, setPhotoIDPreview] = useState(null);
  const [secondaryDocPreview, setSecondaryDocPreview] = useState(null);
  
  // Form fields
  const [formData, setFormData] = useState({
    taxpayerName: '',
    spouseName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    ssn: '',
    spouseSSN: '',
    filingStatus: 'single',
    taxYear: new Date().getFullYear() - 1,
    // Consent checkboxes
    consentToDisclose: false,
    consentToUse: false,
    consentToEfile: false,
    privacyAcknowledged: false,
    feeAgreement: false,
    incomeWaiverAcknowledged: false
  });
  
  // Signature
  const [signature, setSignature] = useState('');
  const [signatureDate, setSignatureDate] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState('type'); // 'draw' or 'type' - default to type for mobile

  const requestId = searchParams.get('id');
  const companyId = searchParams.get('company') || '';
  const preparerId = searchParams.get('preparer') || '';
  // Direct access when there's no request ID but company or preparer params exist
  const isDirectAccess = !requestId && (companyId.length > 0 || preparerId.length > 0);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle photo/document uploads
  const handlePhotoUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'primary') {
          setPhotoIDPreview(reader.result);
        } else {
          setSecondaryDocPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const loadConsentRequest = useCallback(async () => {
    // Direct access mode - no request ID needed (like IRS Waiver form)
    if (isDirectAccess) {
      try {
        if (companyId) {
          const companyData = await getCompanyById(companyId);
          setCompany(companyData);
        }
        if (preparerId) {
          const preparerData = await getPreparerById(preparerId);
          setPreparer(preparerData);
        }
        setLoading(false);
        return;
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load form. Please try again.');
        setLoading(false);
        return;
      }
    }
    
    if (!requestId) {
      setError('Invalid consent request link. Please contact your tax preparer.');
      setLoading(false);
      return;
    }

    try {
      const request = await getConsentRequest(requestId);
      if (!request) {
        setError('Consent request not found or has expired.');
        setLoading(false);
        return;
      }

      if (request.status === 'signed') {
        setSubmitted(true);
        setCurrentStep('submitted');
        setConsentData(request);
        setLoading(false);
        return;
      }

      setConsentData(request);
      
      // Load custom field mappings from localStorage (shared with preparer)
      const savedMappings = localStorage.getItem('pdfFieldMappings');
      if (savedMappings && savedMappings !== 'undefined') {
        try {
          const mappings = JSON.parse(savedMappings);
          setCustomFields(mappings);
          // Initialize custom field values
          const initialValues = {};
          mappings.forEach(field => {
            initialValues[field.id] = field.fieldType === 'checkbox' ? false : '';
          });
          setCustomFieldValues(initialValues);
        } catch (e) {
          console.error('Error parsing field mappings:', e);
        }
      }
      
      // Pre-fill form with any existing data
      if (request.taxpayerName) {
        setFormData(prev => ({
          ...prev,
          taxpayerName: request.taxpayerName || '',
          email: request.taxpayerEmail || '',
          phone: request.taxpayerPhone || '',
          taxYear: request.taxYear || new Date().getFullYear() - 1
        }));
      }

      // Load company and preparer info
      if (request.companyId) {
        const companyData = await getCompanyById(request.companyId);
        setCompany(companyData);
      }
      if (request.preparerId) {
        const preparerData = await getPreparerById(request.preparerId);
        setPreparer(preparerData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading consent request:', err);
      setError('Failed to load consent request. Please try again later.');
      setLoading(false);
    }
  }, [requestId, isDirectAccess, companyId, preparerId]);

  useEffect(() => {
    loadConsentRequest();
  }, [loadConsentRequest]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Canvas signature drawing with proper scaling for responsive canvas
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setSignature(canvasRef.current.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSignature('');
  };

  const allConsentsChecked = () => {
    return formData.consentToDisclose && 
           formData.consentToUse && 
           formData.consentToEfile && 
           formData.privacyAcknowledged && 
           formData.feeAgreement &&
           formData.incomeWaiverAcknowledged;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allConsentsChecked()) {
      alert('Please acknowledge all consent items before signing.');
      return;
    }

    if (!signature && signatureType === 'draw') {
      alert('Please provide your signature.');
      return;
    }

    if (signatureType === 'type' && !formData.taxpayerName) {
      alert('Please type your name as signature.');
      return;
    }

    setSubmitting(true);

    try {
      // Compress photo IDs if present (to reduce Firestore document size)
      let compressedPhotoID = '';
      let compressedSecondaryDoc = '';
      
      if (photoIDPreview) {
        try {
          compressedPhotoID = await compressImage(photoIDPreview, 600, 0.5);
        } catch (e) {
          // Use original but truncated if too large (max ~500KB for Firestore)
          compressedPhotoID = photoIDPreview.length < 500000 ? photoIDPreview : '';
        }
      }
      
      if (secondaryDocPreview) {
        try {
          compressedSecondaryDoc = await compressImage(secondaryDocPreview, 600, 0.5);
        } catch (e) {
          compressedSecondaryDoc = secondaryDocPreview.length < 500000 ? secondaryDocPreview : '';
        }
      }

      // Build custom fields data with labels
      const customFieldsData = {};
      customFields.forEach(field => {
        customFieldsData[field.label] = {
          value: customFieldValues[field.id],
          fieldType: field.fieldType,
          fieldId: field.id
        };
      });

      const signedData = {
        ...formData,
        customFields: customFieldsData,
        signature: signatureType === 'draw' ? signature : `Typed: ${formData.taxpayerName}`,
        signatureType,
        signedAt: new Date().toISOString(),
        signedDate: signatureDate || new Date().toISOString().split('T')[0],
        ipAddress: 'Collected on submission', // In production, get from server
        userAgent: navigator.userAgent,
        taxpayerInfo: {
          name: formData.taxpayerName,
          email: formData.email,
          phone: formData.phone,
          address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`
        },
        taxYear: formData.taxYear,
        // Photo ID uploads for address verification
        documents: {
          photoID: compressedPhotoID,
          secondaryDoc: compressedSecondaryDoc,
          photoIDUploaded: !!photoIDPreview,
          secondaryDocUploaded: !!secondaryDocPreview
        }
      };

      if (isDirectAccess) {
        // Direct submission mode - no request ID
        await submitSignedConsentDirect(signedData, companyId, preparerId, preparer?.name);
      } else {
        // Normal mode - with request ID
        await submitSignedConsent(requestId, signedData);
      }
      setSubmitted(true);
      setCurrentStep('submitted');
    } catch (err) {
      console.error('Error submitting consent:', err);
      alert('Failed to submit consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render custom field input based on type
  const renderCustomField = (field) => {
    const value = customFieldValues[field.id];
    const commonStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', boxSizing: 'border-box' };
    
    switch (field.fieldType) {
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder || ''}
            required={field.required}
            rows={3}
            style={{ ...commonStyle, resize: 'vertical' }}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder || ''}
            required={field.required}
            style={commonStyle}
          />
        );
      case 'currency':
        return (
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}>$</span>
            <input
              type="number"
              step="0.01"
              value={value || ''}
              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
              placeholder={field.placeholder || '0.00'}
              required={field.required}
              style={{ ...commonStyle, paddingLeft: '28px' }}
            />
          </div>
        );
      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
            required={field.required}
            style={commonStyle}
          />
        );
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
            required={field.required}
            style={commonStyle}
          >
            <option value="">Select an option...</option>
            {field.options?.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.checked }))}
              style={{ width: '18px', height: '18px' }}
            />
            <span>{field.placeholder || 'Yes, I confirm'}</span>
          </label>
        );
      case 'address':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder || 'Enter full address'}
            required={field.required}
            rows={2}
            style={{ ...commonStyle, resize: 'vertical' }}
          />
        );
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder || ''}
            required={field.required}
            style={commonStyle}
          />
        );
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <p>Loading consent form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <h2>⚠️ Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted || currentStep === 'submitted') {
    return (
      <div style={styles.container}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✅</div>
          <h2>Thank You!</h2>
          <p>Your consent form has been successfully submitted and signed.</p>
          <p style={styles.successSubtext}>
            A confirmation has been sent to your email. Your tax preparer will contact you shortly.
          </p>
          {company && (
            <div style={styles.companyInfo}>
              <p><strong>{company.name}</strong></p>
              {company.phone && <p>📞 {company.phone}</p>}
              {company.email && <p>📧 {company.email}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step 1: Rules and Regulations Acknowledgment
  if (currentStep === 'rules') {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.formCard }}>
          {/* Header */}
          <div style={styles.header}>
            {company?.logo && <img src={company.logo} alt={company.name} style={styles.logo} />}
            <h1 style={styles.title}>{company?.name || 'Tax Services'}</h1>
            <h2 style={styles.subtitle}>📜 Rules, Regulations & Acknowledgment</h2>
            {preparer && <p style={{ margin: '10px 0 0 0', color: 'rgba(255,255,255,0.8)', fontSize: isMobileView ? '13px' : '14px' }}>Prepared by: {preparer.name}</p>}
          </div>

          <div style={{ padding: isMobileView ? '15px' : '30px' }}>
            {/* Important Notice */}
            <div style={{ background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '12px', padding: isMobileView ? '15px' : '20px', marginBottom: isMobileView ? '15px' : '25px' }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '10px', fontSize: isMobileView ? '15px' : '18px' }}>
                ⚠️ Important Notice
              </h3>
              <p style={{ margin: 0, color: '#78350f', lineHeight: '1.6', fontSize: isMobileView ? '13px' : '14px' }}>
                Before proceeding with the consent form, please carefully read and understand the following rules, 
                regulations, and acknowledgments. Your understanding and acceptance is required to continue.
              </p>
            </div>

            {/* IRS Regulations Section */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: isMobileView ? '15px' : '20px', marginBottom: isMobileView ? '15px' : '20px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobileView ? '14px' : '16px' }}>
                🏛️ IRS Regulations & Compliance
              </h4>
              <ul style={{ margin: 0, paddingLeft: isMobileView ? '18px' : '20px', color: '#334155', lineHeight: '1.8', fontSize: isMobileView ? '13px' : '14px' }}>
                <li><strong>IRC Section 7216:</strong> Federal law requires your consent before your tax return information can be disclosed or used for purposes other than preparing your tax return.</li>
                <li><strong>Privacy Protection:</strong> Your personal information is protected under federal privacy laws and will only be used for tax preparation purposes.</li>
                <li><strong>E-File Authorization:</strong> By signing this form, you authorize electronic filing of your tax return with the IRS.</li>
                <li><strong>Accuracy:</strong> You are responsible for providing accurate and complete information for your tax return.</li>
              </ul>
            </div>

            {/* Terms of Service */}
            <div style={{ background: '#f0f9ff', borderRadius: '12px', padding: isMobileView ? '15px' : '20px', marginBottom: isMobileView ? '15px' : '20px', border: '1px solid #bae6fd' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobileView ? '14px' : '16px' }}>
                📋 Terms of Service
              </h4>
              <ul style={{ margin: 0, paddingLeft: isMobileView ? '18px' : '20px', color: '#334155', lineHeight: '1.8', fontSize: isMobileView ? '13px' : '14px' }}>
                <li>I understand that I am engaging {company?.name || 'this tax service'} to prepare my tax return.</li>
                <li>I agree to provide all necessary documentation and information required for accurate tax preparation.</li>
                <li>I understand that the tax preparer will provide advice based on the information I provide.</li>
                <li>I acknowledge that tax laws change and my preparer will apply current laws to my situation.</li>
                <li>I agree to review my completed tax return before it is filed.</li>
              </ul>
            </div>

            {/* Fee Agreement */}
            <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: isMobileView ? '15px' : '20px', marginBottom: isMobileView ? '15px' : '20px', border: '1px solid #bbf7d0' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobileView ? '14px' : '16px' }}>
                💰 Fee Agreement & Payment Terms
              </h4>
              <ul style={{ margin: 0, paddingLeft: isMobileView ? '18px' : '20px', color: '#334155', lineHeight: '1.8', fontSize: isMobileView ? '13px' : '14px' }}>
                <li>I understand that fees for tax preparation services will be discussed and agreed upon.</li>
                <li>I agree to pay all fees associated with the preparation of my tax return.</li>
                <li>I understand that additional fees may apply for amended returns or additional services.</li>
                <li>Payment is due upon completion of services unless otherwise arranged.</li>
              </ul>
            </div>

            {/* Privacy Policy */}
            <div style={{ background: '#faf5ff', borderRadius: '12px', padding: isMobileView ? '15px' : '20px', marginBottom: isMobileView ? '15px' : '25px', border: '1px solid #e9d5ff' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobileView ? '14px' : '16px' }}>
                🔒 Privacy Policy
              </h4>
              <ul style={{ margin: 0, paddingLeft: isMobileView ? '18px' : '20px', color: '#334155', lineHeight: '1.8', fontSize: isMobileView ? '13px' : '14px' }}>
                <li>Your personal and financial information will be kept strictly confidential.</li>
                <li>Information will only be shared as required by law or with your explicit consent.</li>
                <li>Electronic records are stored securely and protected against unauthorized access.</li>
                <li>You have the right to request copies of your tax documents at any time.</li>
              </ul>
            </div>

            {/* Acknowledgment Checkbox */}
            <div style={{ background: '#1e293b', borderRadius: '12px', padding: isMobileView ? '18px' : '20px', marginBottom: isMobileView ? '15px' : '25px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: isMobileView ? '12px' : '15px', cursor: 'pointer', color: 'white' }}>
                <input
                  type="checkbox"
                  checked={rulesAccepted}
                  onChange={(e) => setRulesAccepted(e.target.checked)}
                  style={{ width: isMobileView ? '28px' : '24px', height: isMobileView ? '28px' : '24px', marginTop: '2px', accentColor: '#10b981', flexShrink: 0 }}
                />
                <span style={{ lineHeight: '1.6', fontSize: isMobileView ? '14px' : '15px' }}>
                  <strong style={{ color: '#10b981' }}>I ACKNOWLEDGE AND AGREE:</strong> I have read and understand all the rules, 
                  regulations, terms of service, fee agreement, and privacy policy stated above. I agree to comply with 
                  all requirements and understand my responsibilities as a taxpayer.
                </span>
              </label>
            </div>

            {/* Continue Button */}
            <button
              onClick={() => {
                if (!rulesAccepted) {
                  alert('Please read and accept the rules and regulations before continuing.');
                  return;
                }
                setCurrentStep('form');
              }}
              disabled={!rulesAccepted}
              style={{
                width: '100%',
                padding: isMobileView ? '16px' : '18px',
                background: rulesAccepted ? 'linear-gradient(135deg, #10b981, #059669)' : '#94a3b8',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: isMobileView ? '1rem' : '1.1rem',
                fontWeight: '700',
                cursor: rulesAccepted ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: rulesAccepted ? '0 4px 15px rgba(16, 185, 129, 0.4)' : 'none'
              }}
            >
              {rulesAccepted ? '✓ I Agree - Continue to Form' : 'Please Accept Terms Above'}
              {rulesAccepted && <span>→</span>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Main Form - uses isMobileView from state
  return (
    <div style={styles.container}>
      <div style={styles.formCard}>
        {/* Header */}
        <div style={styles.header}>
          {company?.logo && <img src={company.logo} alt={company.name} style={styles.logo} />}
          <h1 style={styles.title}>{company?.name || 'Tax Services'}</h1>
          <h2 style={styles.subtitle}>Taxpayer Consent & Authorization Form</h2>
          <p style={styles.taxYear}>Tax Year: {formData.taxYear}</p>
          {preparer && <p style={{ margin: '5px 0 0 0', color: 'rgba(255,255,255,0.8)', fontSize: isMobileView ? '13px' : '14px' }}>Preparer: {preparer.name}</p>}
        </div>

        {/* Progress Indicator */}
        {/* Progress Indicator */}
        <div style={{ padding: isMobileView ? '12px 15px' : '15px 30px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobileView ? '6px' : '10px', flexWrap: 'wrap', justifyContent: isMobileView ? 'center' : 'flex-start' }}>
            <span style={{ background: '#10b981', color: 'white', width: isMobileView ? '22px' : '24px', height: isMobileView ? '22px' : '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', flexShrink: 0 }}>✓</span>
            <span style={{ color: '#166534', fontWeight: '500', fontSize: isMobileView ? '12px' : '14px' }}>Rules Accepted</span>
            <span style={{ color: '#d1d5db' }}>→</span>
            <span style={{ background: '#3b82f6', color: 'white', width: isMobileView ? '22px' : '24px', height: isMobileView ? '22px' : '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', flexShrink: 0 }}>2</span>
            <span style={{ color: '#1d4ed8', fontWeight: '500', fontSize: isMobileView ? '12px' : '14px' }}>Fill Form & Sign</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Custom Fields Section (if any) */}
          {customFields.length > 0 && (
            <section style={{ ...styles.section, background: '#faf5ff', borderBottom: '2px solid #c4b5fd' }}>
              <h3 style={{ ...styles.sectionTitle, color: '#6d28d9' }}>📝 Additional Information Required</h3>
              <p style={{ marginTop: 0, marginBottom: isMobileView ? '15px' : '20px', color: '#64748b', fontSize: isMobileView ? '13px' : '14px' }}>
                Please fill out the following fields as requested by your tax preparer.
              </p>
              <div style={{ display: 'grid', gap: isMobileView ? '15px' : '20px' }}>
                {customFields.map((field) => (
                  <div key={field.id} style={styles.formGroup}>
                    <label style={styles.label}>
                      {field.label} {field.required && <span style={{ color: '#dc2626' }}>*</span>}
                    </label>
                    {renderCustomField(field)}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Personal Information Section */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>📋 Taxpayer Information</h3>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Taxpayer Full Name *</label>
                <input
                  type="text"
                  name="taxpayerName"
                  value={formData.taxpayerName}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  placeholder="As shown on your Social Security card"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Social Security Number *</label>
                <input
                  type="text"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Spouse Full Name (if filing jointly)</label>
                <input
                  type="text"
                  name="spouseName"
                  value={formData.spouseName}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Spouse SSN</label>
                <input
                  type="text"
                  name="spouseSSN"
                  value={formData.spouseSSN}
                  onChange={handleInputChange}
                  style={styles.input}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Street Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  maxLength={2}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>ZIP Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                  maxLength={10}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Filing Status *</label>
              <select
                name="filingStatus"
                value={formData.filingStatus}
                onChange={handleInputChange}
                required
                style={styles.select}
              >
                <option value="single">Single</option>
                <option value="married_joint">Married Filing Jointly</option>
                <option value="married_separate">Married Filing Separately</option>
                <option value="head_of_household">Head of Household</option>
                <option value="qualifying_widow">Qualifying Widow(er)</option>
              </select>
            </div>
          </section>

          {/* Photo ID Upload Section */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>📷 ID & Address Verification</h3>
            
            <div style={{ background: '#fef3c7', padding: isMobileView ? '12px' : '15px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #fbbf24' }}>
              <p style={{ margin: 0, fontSize: isMobileView ? '13px' : '14px', color: '#92400e', lineHeight: '1.5' }}>
                <strong>📋 Required:</strong> Please upload a valid government-issued photo ID (Driver's License, State ID, or Passport) showing your current address. You may also upload a secondary document (utility bill, bank statement) for additional verification.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobileView ? '1fr' : 'repeat(2, 1fr)', gap: '20px' }}>
              {/* Primary Photo ID */}
              <div>
                <h4 style={{ fontSize: isMobileView ? '14px' : '16px', color: '#1e3a8a', marginBottom: '10px', fontWeight: '600' }}>
                  🪪 Photo ID Upload *
                </h4>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                  (Driver's License, State ID, Passport - JPG, PNG, PDF)
                </p>
                <div 
                  style={{
                    border: photoIDPreview ? '2px solid #22c55e' : '2px dashed #94a3b8',
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: photoIDPreview ? '#f0fdf4' : '#f8fafc',
                    transition: 'all 0.2s',
                    minHeight: isMobileView ? '120px' : '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => document.getElementById('photoIDInput').click()}
                >
                  {photoIDPreview ? (
                    <div>
                      {photoIDPreview.startsWith('data:image') ? (
                        <img 
                          src={photoIDPreview} 
                          alt="Uploaded ID" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: isMobileView ? '100px' : '120px', 
                            borderRadius: '8px',
                            objectFit: 'contain'
                          }} 
                        />
                      ) : (
                        <div style={{ fontSize: '40px', marginBottom: '10px' }}>📄</div>
                      )}
                      <p style={{ marginTop: '10px', color: '#22c55e', fontWeight: '600', fontSize: isMobileView ? '13px' : '14px' }}>✓ Photo ID uploaded</p>
                      <p style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>Tap to change</p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: isMobileView ? '35px' : '45px', marginBottom: '10px' }}>📤</div>
                      <p style={{ color: '#64748b', fontSize: isMobileView ? '13px' : '14px', fontWeight: '500' }}>Tap to upload Photo ID</p>
                      <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '5px' }}>Required for verification</p>
                    </div>
                  )}
                </div>
                <input
                  id="photoIDInput"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.rtf,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.heic"
                  onChange={(e) => handlePhotoUpload(e, 'primary')}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Secondary Document */}
              <div>
                <h4 style={{ fontSize: isMobileView ? '14px' : '16px', color: '#1e3a8a', marginBottom: '10px', fontWeight: '600' }}>
                  📄 Secondary Document (Optional)
                </h4>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                  (Utility bill, Bank statement, etc.)
                </p>
                <div 
                  style={{
                    border: secondaryDocPreview ? '2px solid #22c55e' : '2px dashed #94a3b8',
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: secondaryDocPreview ? '#f0fdf4' : '#f8fafc',
                    transition: 'all 0.2s',
                    minHeight: isMobileView ? '120px' : '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => document.getElementById('secondaryDocInput').click()}
                >
                  {secondaryDocPreview ? (
                    <div>
                      {secondaryDocPreview.startsWith('data:image') ? (
                        <img 
                          src={secondaryDocPreview} 
                          alt="Secondary Doc" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: isMobileView ? '100px' : '120px', 
                            borderRadius: '8px',
                            objectFit: 'contain'
                          }} 
                        />
                      ) : (
                        <div style={{ fontSize: '40px', marginBottom: '10px' }}>📄</div>
                      )}
                      <p style={{ marginTop: '10px', color: '#22c55e', fontWeight: '600', fontSize: isMobileView ? '13px' : '14px' }}>✓ Document uploaded</p>
                      <p style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>Tap to change</p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: isMobileView ? '35px' : '45px', marginBottom: '10px' }}>📤</div>
                      <p style={{ color: '#64748b', fontSize: isMobileView ? '13px' : '14px', fontWeight: '500' }}>Tap to upload document</p>
                      <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '5px' }}>Optional - for extra verification</p>
                    </div>
                  )}
                </div>
                <input
                  id="secondaryDocInput"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.rtf,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.heic"
                  onChange={(e) => handlePhotoUpload(e, 'secondary')}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '15px', fontStyle: 'italic', lineHeight: '1.5' }}>
              🔒 Your documents are encrypted and stored securely. They will only be used for identity and address verification purposes.
            </p>
          </section>

          {/* Consent Section */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>📝 Consent & Authorization</h3>

            {/* Consent to Disclose - IRC §7216 */}
            <div style={styles.consentItem}>
              <div style={styles.consentHeader}>
                <input
                  type="checkbox"
                  name="consentToDisclose"
                  checked={formData.consentToDisclose}
                  onChange={handleInputChange}
                  id="consentToDisclose"
                  style={styles.checkbox}
                />
                <label htmlFor="consentToDisclose" style={styles.consentLabel}>
                  <strong>Consent to Disclose Tax Return Information</strong>
                </label>
              </div>
              <div style={styles.consentText}>
                <p>
                  Under Internal Revenue Code (IRC) §7216, I authorize {company?.name || 'the tax preparer'} to disclose 
                  my tax return information to the IRS and state tax authorities for the purpose of filing my tax return. 
                  This consent includes disclosure to third-party service providers necessary for tax return preparation 
                  and electronic filing.
                </p>
                <p style={styles.smallText}>
                  This consent is valid for the current tax year and expires on December 31 of the following year, 
                  unless revoked earlier in writing.
                </p>
              </div>
            </div>

            {/* Consent to Use */}
            <div style={styles.consentItem}>
              <div style={styles.consentHeader}>
                <input
                  type="checkbox"
                  name="consentToUse"
                  checked={formData.consentToUse}
                  onChange={handleInputChange}
                  id="consentToUse"
                  style={styles.checkbox}
                />
                <label htmlFor="consentToUse" style={styles.consentLabel}>
                  <strong>Consent to Use Tax Return Information</strong>
                </label>
              </div>
              <div style={styles.consentText}>
                <p>
                  I authorize {company?.name || 'the tax preparer'} to use my tax return information for the 
                  purpose of providing tax preparation services, including but not limited to: reviewing prior 
                  year returns, providing tax planning advice, and communicating about tax-related matters.
                </p>
              </div>
            </div>

            {/* E-file Authorization */}
            <div style={styles.consentItem}>
              <div style={styles.consentHeader}>
                <input
                  type="checkbox"
                  name="consentToEfile"
                  checked={formData.consentToEfile}
                  onChange={handleInputChange}
                  id="consentToEfile"
                  style={styles.checkbox}
                />
                <label htmlFor="consentToEfile" style={styles.consentLabel}>
                  <strong>IRS E-file Authorization</strong>
                </label>
              </div>
              <div style={styles.consentText}>
                <p>
                  I authorize {company?.name || 'the tax preparer'} to electronically file my federal and state 
                  income tax returns. I understand that by signing this form, I am authorizing the use of my 
                  electronic signature for IRS Form 8879 (IRS e-file Signature Authorization).
                </p>
                <p>
                  I declare that I have examined my tax return and accompanying schedules and statements, and to 
                  the best of my knowledge and belief, they are true, correct, and complete.
                </p>
              </div>
            </div>

            {/* Income Documentation Waiver */}
            <div style={styles.consentItem}>
              <div style={styles.consentHeader}>
                <input
                  type="checkbox"
                  name="incomeWaiverAcknowledged"
                  checked={formData.incomeWaiverAcknowledged}
                  onChange={handleInputChange}
                  id="incomeWaiverAcknowledged"
                  style={styles.checkbox}
                />
                <label htmlFor="incomeWaiverAcknowledged" style={styles.consentLabel}>
                  <strong>Income Documentation Acknowledgment</strong>
                </label>
              </div>
              <div style={styles.consentText}>
                <p>
                  I acknowledge that I am responsible for providing accurate and complete income documentation. 
                  I understand that:
                </p>
                <ul style={styles.bulletList}>
                  <li>I must provide all W-2s, 1099s, and other income documents</li>
                  <li>Failure to report all income may result in IRS penalties and interest</li>
                  <li>The tax preparer relies on information I provide and is not responsible for unreported income</li>
                  <li>I am ultimately responsible for the accuracy of my tax return</li>
                  <li>Intentional omission of income is tax fraud and is punishable by law</li>
                </ul>
              </div>
            </div>

            {/* Privacy Policy */}
            <div style={styles.consentItem}>
              <div style={styles.consentHeader}>
                <input
                  type="checkbox"
                  name="privacyAcknowledged"
                  checked={formData.privacyAcknowledged}
                  onChange={handleInputChange}
                  id="privacyAcknowledged"
                  style={styles.checkbox}
                />
                <label htmlFor="privacyAcknowledged" style={styles.consentLabel}>
                  <strong>Privacy Policy Acknowledgment</strong>
                </label>
              </div>
              <div style={styles.consentText}>
                <p>
                  I acknowledge that I have been informed of the privacy policies regarding my tax return 
                  information. I understand that my personal information will be:
                </p>
                <ul style={styles.bulletList}>
                  <li>Kept confidential and secure</li>
                  <li>Used only for tax preparation purposes unless otherwise authorized</li>
                  <li>Retained for a minimum of 3 years as required by law</li>
                  <li>Protected with appropriate security measures</li>
                </ul>
              </div>
            </div>

            {/* Fee Agreement */}
            <div style={styles.consentItem}>
              <div style={styles.consentHeader}>
                <input
                  type="checkbox"
                  name="feeAgreement"
                  checked={formData.feeAgreement}
                  onChange={handleInputChange}
                  id="feeAgreement"
                  style={styles.checkbox}
                />
                <label htmlFor="feeAgreement" style={styles.consentLabel}>
                  <strong>Fee Agreement & Service Terms</strong>
                </label>
              </div>
              <div style={styles.consentText}>
                <p>
                  I understand and agree to the following:
                </p>
                <ul style={styles.bulletList}>
                  <li>Tax preparation fees will be disclosed before services are rendered</li>
                  <li>Fees are based on the complexity of my return and forms required</li>
                  <li>Additional fees may apply for amended returns or audit representation</li>
                  <li>Payment is due upon completion of tax return preparation</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Signature Section */}
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>✍️ Signature</h3>
            
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setSignatureType('draw')}
                style={{
                  padding: isMobileView ? '14px 20px' : '12px 24px',
                  border: signatureType === 'draw' ? '2px solid #2563eb' : '2px solid #e2e8f0',
                  borderRadius: '10px',
                  backgroundColor: signatureType === 'draw' ? '#2563eb' : 'white',
                  color: signatureType === 'draw' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  fontSize: isMobileView ? '15px' : '14px',
                  fontWeight: '600',
                  flex: isMobileView ? '1' : 'none',
                  minWidth: isMobileView ? '45%' : 'auto',
                  transition: 'all 0.2s'
                }}
              >
                ✍️ Draw Signature
              </button>
              <button
                type="button"
                onClick={() => setSignatureType('type')}
                style={{
                  padding: isMobileView ? '14px 20px' : '12px 24px',
                  border: signatureType === 'type' ? '2px solid #2563eb' : '2px solid #e2e8f0',
                  borderRadius: '10px',
                  backgroundColor: signatureType === 'type' ? '#2563eb' : 'white',
                  color: signatureType === 'type' ? 'white' : '#64748b',
                  cursor: 'pointer',
                  fontSize: isMobileView ? '15px' : '14px',
                  fontWeight: '600',
                  flex: isMobileView ? '1' : 'none',
                  minWidth: isMobileView ? '45%' : 'auto',
                  transition: 'all 0.2s'
                }}
              >
                ⌨️ Type Signature
              </button>
            </div>

            {signatureType === 'draw' ? (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ marginBottom: '10px', fontWeight: '500', color: '#444', fontSize: isMobileView ? '14px' : '16px' }}>
                  Sign below using your {isMobileView ? 'finger' : 'mouse or finger'}:
                </p>
                <canvas
                  ref={canvasRef}
                  width={isMobileView ? 300 : 500}
                  height={isMobileView ? 120 : 150}
                  style={{
                    border: '2px dashed #94a3b8',
                    borderRadius: '12px',
                    backgroundColor: '#fafafa',
                    cursor: 'crosshair',
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    height: isMobileView ? '120px' : '150px',
                    touchAction: 'none'
                  }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <button 
                  type="button" 
                  onClick={clearSignature} 
                  style={{
                    marginTop: '12px',
                    padding: isMobileView ? '14px 24px' : '10px 20px',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    width: isMobileView ? '100%' : 'auto'
                  }}
                >
                  🗑️ Clear Signature
                </button>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ marginBottom: '10px', fontWeight: '500', color: '#444', fontSize: isMobileView ? '14px' : '16px' }}>
                  Type your full legal name as your electronic signature:
                </p>
                <input
                  type="text"
                  value={formData.taxpayerName}
                  readOnly
                  style={{
                    width: '100%',
                    padding: isMobileView ? '16px' : '18px',
                    border: '2px solid #2563eb',
                    borderRadius: '10px',
                    fontSize: isMobileView ? '22px' : '28px',
                    fontFamily: '"Brush Script MT", "Dancing Script", cursive',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    boxSizing: 'border-box',
                    color: '#1e293b'
                  }}
                />
                <p style={{ fontSize: isMobileView ? '12px' : '13px', color: '#64748b', marginTop: '10px', fontStyle: 'italic', lineHeight: '1.5' }}>
                  By typing your name above, you agree that this electronic signature is the legal equivalent 
                  of your manual signature.
                </p>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Date *</label>
              <input
                type="date"
                value={signatureDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setSignatureDate(e.target.value)}
                required
                style={{
                  ...styles.input,
                  padding: isMobileView ? '14px' : '12px',
                  fontSize: isMobileView ? '16px' : '15px'
                }}
              />
            </div>
          </section>

          {/* Preparer Information */}
          {preparer && (
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>👤 Your Tax Preparer</h3>
              <div style={{
                display: 'flex',
                alignItems: isMobileView ? 'center' : 'center',
                flexDirection: isMobileView ? 'column' : 'row',
                gap: isMobileView ? '15px' : '20px',
                padding: '20px',
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                textAlign: isMobileView ? 'center' : 'left',
                border: '1px solid #e2e8f0'
              }}>
                {preparer.photo && <img src={preparer.photo} alt={preparer.name} style={{
                  width: isMobileView ? '60px' : '70px',
                  height: isMobileView ? '60px' : '70px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }} />}
                <div>
                  <p style={{ margin: '0 0 5px 0', fontWeight: '600', fontSize: isMobileView ? '16px' : '18px', color: '#1e293b' }}>{preparer.name}</p>
                  {preparer.email && <p style={{ margin: '4px 0', color: '#64748b', fontSize: isMobileView ? '14px' : '15px' }}>📧 {preparer.email}</p>}
                  {preparer.phone && <p style={{ margin: '4px 0', color: '#64748b', fontSize: isMobileView ? '14px' : '15px' }}>📞 {preparer.phone}</p>}
                </div>
              </div>
            </section>
          )}

          {/* Submit Button */}
          <div style={{ padding: isMobileView ? '25px 15px' : '30px', textAlign: 'center' }}>
            <button
              type="submit"
              disabled={submitting || !allConsentsChecked()}
              style={{
                padding: isMobileView ? '18px 30px' : '16px 50px',
                fontSize: isMobileView ? '17px' : '18px',
                fontWeight: '700',
                color: 'white',
                backgroundColor: allConsentsChecked() && !submitting ? '#10b981' : '#94a3b8',
                border: 'none',
                borderRadius: '12px',
                cursor: allConsentsChecked() && !submitting ? 'pointer' : 'not-allowed',
                width: isMobileView ? '100%' : 'auto',
                transition: 'all 0.2s',
                boxShadow: allConsentsChecked() && !submitting ? '0 4px 15px rgba(16, 185, 129, 0.4)' : 'none'
              }}
            >
              {submitting ? '⏳ Submitting...' : '✅ Sign & Submit Consent Form'}
            </button>
            {!allConsentsChecked() && (
              <p style={{ color: '#ef4444', marginTop: '15px', fontSize: isMobileView ? '13px' : '14px', fontWeight: '500' }}>
                ⚠️ Please acknowledge all consent items above before signing.
              </p>
            )}
          </div>
        </form>

        {/* Footer */}
        <footer style={{ 
          padding: isMobileView ? '20px 15px' : '20px 30px', 
          backgroundColor: '#f1f5f9', 
          textAlign: 'center', 
          fontSize: isMobileView ? '12px' : '13px', 
          color: '#64748b',
          borderTop: '1px solid #e2e8f0'
        }}>
          <p style={{ margin: '0 0 8px 0' }}>🔒 This form is protected by industry-standard encryption.</p>
          <p style={{ margin: 0 }}>© {new Date().getFullYear()} {company?.name || 'Tax Services'}. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

// Helper function to detect mobile
const isMobile = () => window.innerWidth <= 768;

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f7fa',
    padding: isMobile() ? '10px' : '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxSizing: 'border-box'
  },
  loadingCard: {
    maxWidth: '400px',
    margin: isMobile() ? '50px auto' : '100px auto',
    padding: isMobile() ? '25px' : '40px',
    backgroundColor: 'white',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite'
  },
  errorCard: {
    maxWidth: '500px',
    margin: isMobile() ? '30px 10px' : '100px auto',
    padding: isMobile() ? '25px' : '40px',
    backgroundColor: '#fff5f5',
    borderRadius: '12px',
    textAlign: 'center',
    border: '1px solid #fed7d7'
  },
  successCard: {
    maxWidth: '500px',
    margin: isMobile() ? '30px 10px' : '100px auto',
    padding: isMobile() ? '25px' : '40px',
    backgroundColor: '#f0fff4',
    borderRadius: '12px',
    textAlign: 'center',
    border: '1px solid #9ae6b4'
  },
  successIcon: {
    fontSize: isMobile() ? '50px' : '60px',
    marginBottom: '20px'
  },
  successSubtext: {
    color: '#666',
    marginTop: '10px',
    fontSize: isMobile() ? '14px' : '16px'
  },
  companyInfo: {
    marginTop: '30px',
    padding: isMobile() ? '15px' : '20px',
    backgroundColor: 'white',
    borderRadius: '8px'
  },
  formCard: {
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: isMobile() ? '8px' : '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  header: {
    backgroundColor: '#2563eb',
    color: 'white',
    padding: isMobile() ? '20px 15px' : '30px',
    textAlign: 'center'
  },
  logo: {
    width: isMobile() ? '60px' : '80px',
    height: isMobile() ? '60px' : '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    marginBottom: '15px',
    border: '3px solid white'
  },
  title: {
    margin: '0 0 10px 0',
    fontSize: isMobile() ? '20px' : '28px',
    fontWeight: '600',
    wordWrap: 'break-word'
  },
  subtitle: {
    margin: '0 0 10px 0',
    fontSize: isMobile() ? '14px' : '18px',
    fontWeight: '400',
    opacity: '0.9'
  },
  taxYear: {
    margin: '0',
    fontSize: isMobile() ? '14px' : '16px',
    opacity: '0.8'
  },
  section: {
    padding: isMobile() ? '15px' : '25px 30px',
    borderBottom: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: isMobile() ? '16px' : '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: isMobile() ? '15px' : '20px',
    paddingBottom: '10px',
    borderBottom: '2px solid #2563eb'
  },
  formRow: {
    display: 'flex',
    gap: isMobile() ? '10px' : '15px',
    flexWrap: 'wrap',
    flexDirection: isMobile() ? 'column' : 'row'
  },
  formGroup: {
    flex: '1',
    minWidth: isMobile() ? '100%' : '200px',
    marginBottom: isMobile() ? '12px' : '15px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#444',
    fontSize: isMobile() ? '13px' : '14px'
  },
  input: {
    width: '100%',
    padding: isMobile() ? '12px 10px' : '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: isMobile() ? '16px' : '15px', // 16px prevents zoom on iOS
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    WebkitAppearance: 'none'
  },
  select: {
    width: '100%',
    padding: isMobile() ? '12px 10px' : '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: isMobile() ? '16px' : '15px',
    backgroundColor: 'white',
    cursor: 'pointer',
    boxSizing: 'border-box'
  },
  consentItem: {
    marginBottom: isMobile() ? '15px' : '25px',
    padding: isMobile() ? '12px' : '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  consentHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: isMobile() ? '10px' : '12px',
    marginBottom: '10px'
  },
  checkbox: {
    width: isMobile() ? '24px' : '20px',
    height: isMobile() ? '24px' : '20px',
    cursor: 'pointer',
    marginTop: '2px',
    flexShrink: 0
  },
  consentLabel: {
    cursor: 'pointer',
    fontSize: isMobile() ? '14px' : '15px',
    lineHeight: '1.4'
  },
  consentText: {
    marginLeft: isMobile() ? '34px' : '32px',
    fontSize: isMobile() ? '13px' : '14px',
    color: '#555',
    lineHeight: '1.6'
  },
  smallText: {
    fontSize: isMobile() ? '11px' : '12px',
    color: '#777',
    marginTop: '8px',
    fontStyle: 'italic'
  },
  bulletList: {
    margin: '10px 0',
    paddingLeft: isMobile() ? '15px' : '20px'
  },
  signatureToggle: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  toggleButton: {
    padding: isMobile() ? '12px 16px' : '10px 20px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s',
    flex: isMobile() ? '1' : 'none',
    minWidth: isMobile() ? '45%' : 'auto'
  },
  toggleButtonActive: {
    backgroundColor: '#2563eb',
    color: 'white',
    borderColor: '#2563eb'
  },
  signatureArea: {
    marginBottom: '20px'
  },
  signatureLabel: {
    marginBottom: '10px',
    fontWeight: '500',
    color: '#444',
    fontSize: isMobile() ? '14px' : '16px'
  },
  canvas: {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    cursor: 'crosshair',
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    height: isMobile() ? '120px' : '150px',
    touchAction: 'none' // Prevents scrolling while drawing
  },
  clearButton: {
    marginTop: '10px',
    padding: isMobile() ? '12px 20px' : '8px 16px',
    border: '1px solid #dc3545',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#dc3545',
    cursor: 'pointer',
    fontSize: '14px',
    width: isMobile() ? '100%' : 'auto'
  },
  typedSignature: {
    marginBottom: '20px'
  },
  typedSignatureInput: {
    width: '100%',
    padding: isMobile() ? '12px' : '15px',
    border: '2px solid #2563eb',
    borderRadius: '6px',
    fontSize: isMobile() ? '20px' : '24px',
    fontFamily: '"Brush Script MT", cursive',
    textAlign: 'center',
    backgroundColor: '#f8f9fa',
    boxSizing: 'border-box'
  },
  preparerInfo: {
    display: 'flex',
    alignItems: isMobile() ? 'flex-start' : 'center',
    flexDirection: isMobile() ? 'column' : 'row',
    gap: isMobile() ? '15px' : '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    textAlign: isMobile() ? 'center' : 'left'
  },
  preparerPhoto: {
    width: isMobile() ? '50px' : '60px',
    height: isMobile() ? '50px' : '60px',
    borderRadius: '50%',
    objectFit: 'cover',
    margin: isMobile() ? '0 auto' : '0'
  },
  submitSection: {
    padding: isMobile() ? '20px 15px' : '30px',
    textAlign: 'center'
  },
  submitButton: {
    padding: isMobile() ? '16px 24px' : '15px 40px',
    fontSize: isMobile() ? '16px' : '18px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#28a745',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: isMobile() ? '100%' : 'auto'
  },
  submitButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  warningText: {
    color: '#dc3545',
    marginTop: '15px',
    fontSize: isMobile() ? '13px' : '14px'
  },
  footer: {
    padding: isMobile() ? '15px' : '20px 30px',
    backgroundColor: '#f8f9fa',
    textAlign: 'center',
    fontSize: isMobile() ? '11px' : '12px',
    color: '#777'
  }
};

export default TaxpayerConsentForm;
